// src/components/prescriptions/TemplatesManager.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  getStats, getBranding,
} from '../../services/prescriptionsApi';
import TemplateForm from './TemplateForm';
import PreviewPaper from './PreviewPaper';

// PT-BR labels
const TYPE_LABEL_PT = { medication: 'Medicação', exam: 'Solicitação de exame', guidance: 'Orientações', certificate: 'Atestado' };
const CATEGORY_LABEL_PT = { general: 'geral', surgery: 'cirurgia', orthodontics: 'ortodontia', implant: 'implantes', endodontics: 'endodontia', periodontics: 'periodontia', pediatric: 'odontopediatria', prosthesis: 'prótese' };
const labelType = (t) => TYPE_LABEL_PT[t] || t || '';
const labelCategory = (c) => CATEGORY_LABEL_PT[c] || c || '';
const isDefaultTemplate = (tpl) => Boolean(tpl?.is_default || tpl?.is_system || tpl?.source === 'default');
const getId = (obj) => obj?.id || obj?._id;

export default function TemplatesManager() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [branding, setBranding] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    template_name: '', template_type: 'medication', title: '', category: 'general',
    medications: [], exam_requests: [], guidance_text: '', certificate_text: ''
  });

  const isEditing = useMemo(() => Boolean(getId(selected)), [selected]);
  const selectedIsDefault = useMemo(() => isDefaultTemplate(selected), [selected]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tpl, st, br] = await Promise.all([listTemplates(), getStats(), getBranding()]);
      setItems(Array.isArray(tpl) ? tpl : []);
      setStats(st);
      setBranding(br || {});
    } catch (e) { console.error(e); toast.error('Falha ao carregar templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const onOpenNew = () => {
    setSelected(null);
    setForm({ template_name: '', template_type: 'medication', title: '', category: 'general', medications: [], exam_requests: [], guidance_text: '', certificate_text: '' });
    setShowModal(true);
  };
  const onOpenEdit = (item) => {
    setSelected(item);
    setForm({
      template_name: item.template_name || '', template_type: item.template_type || 'medication',
      title: item.title || '', category: item.category || 'general',
      medications: item.medications || [], exam_requests: item.exam_requests || [],
      guidance_text: item.guidance_text || '', certificate_text: item.certificate_text || '',
    });
    setShowModal(true);
  };
  const onClose = () => { setShowModal(false); setSelected(null); };

  const fallbackCreateFromDefault = async () => {
    const payload = { ...form };
    if (!payload.template_name || payload.template_name === selected?.template_name)
      payload.template_name = `${selected?.template_name || 'Template'} (personalizado)`;
    return await createTemplate(payload);
  };

  const onSave = async (e) => {
    e?.preventDefault?.(); setLoading(true);
    try {
      if (isEditing) {
        if (selectedIsDefault) { await fallbackCreateFromDefault(); toast.success('Cópia personalizada criada a partir do template padrão.'); onClose(); await fetchAll(); return; }
        await updateTemplate(getId(selected), form); toast.success('Template atualizado!');
      } else { await createTemplate(form); toast.success('Template criado!'); }
      onClose(); await fetchAll();
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : '';
      const lc = detail?.toLowerCase?.() || '';
      const looksLocked = [403, 409, 423].includes(status) || lc.includes('default') || lc.includes('read-only') || lc.includes('somente leitura');
      try {
        if (isEditing && (selectedIsDefault || looksLocked)) { await fallbackCreateFromDefault(); toast.success('Template padrão é protegido; foi criada uma cópia personalizada.'); onClose(); await fetchAll(); return; }
      } catch (e2) { console.error('Fallback creation failed:', e2); }
      toast.error(detail || 'Erro ao salvar template');
    } finally { setLoading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir este template?')) return;
    setLoading(true);
    try { await deleteTemplate(id); toast.success('Template excluído'); await fetchAll(); }
    catch (e) { console.error(e); toast.error('Falha ao excluir template'); }
    finally { setLoading(false); }
  };

    return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Templates</h2>
        <div className="flex items-center gap-2">
          <button onClick={onOpenNew} className="px-3 py-2 bg-purple-600 text-white rounded-md">Novo template</button>
        </div>
      </div>

      {stats && <div className="text-xs text-gray-500 mb-3">Total: {stats?.templates_total ?? 0} • Atualizados: {stats?.templates_updated ?? 0}</div>}

      {loading && items.length === 0 ? <div>Carregando...</div> : (
        <div className="space-y-2">
          {items.length === 0 && <div className="text-sm text-gray-500">Nenhum template encontrado.</div>}
          {items.map((t) => (
            <div key={getId(t)} className="border rounded-md p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">
                  {t.template_name}{' '}
                  {isDefaultTemplate(t) && <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-gray-100 border text-gray-600 align-middle">Padrão do sistema</span>}
                </div>
                <div className="text-xs text-gray-500">{labelType(t.template_type)} • {labelCategory(t.category || 'geral')}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onOpenEdit(t)} className="px-3 py-1 border rounded-md">{isDefaultTemplate(t) ? 'Usar como base' : 'Editar'}</button>
                {!isDefaultTemplate(t) && <button onClick={() => onDelete(getId(t))} className="px-3 py-1 border rounded-md">Excluir</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal com pré-visualização e rolagem interna */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/30 p-4 sm:p-6 flex items-center justify-center z-50"
          onClick={onClose}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-lg p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{isEditing ? (selectedIsDefault ? 'Criar cópia personalizada' : 'Editar Template') : 'Novo Template'}</h3>
              <button onClick={onClose} className="px-3 py-1 border rounded-md">Fechar</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <form onSubmit={onSave} className="space-y-4">
                <TemplateForm form={form} setForm={setForm} />
                {selectedIsDefault && (
                  <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 p-2 rounded">
                    Este é um template padrão protegido. Ao salvar, criaremos uma <b>cópia personalizada</b>.
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={onClose} className="px-3 py-2 border rounded-md">Cancelar</button>
                  <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-md">{loading ? 'Salvando...' : (selectedIsDefault ? 'Salvar cópia' : 'Salvar')}</button>
                </div>
              </form>

              <div className="border rounded-md overflow-auto max-h-[70vh]">
                <PreviewPaper
                  branding={branding || {}}
                  content={{ title: form.title || form.template_name, guidance_text: form.guidance_text || form.certificate_text }}
                  variant="template"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}